<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue'
import { useWindowScroll } from '@vueuse/core'
import { register, type SwiperContainer } from 'swiper/element/bundle'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import MercariSearchBar from '@/components/ui/mercari/MercariSearchBar.vue'
import { useBreakpoints, getVueAssetImagePath } from '@/lib/utils'
import { getMercariUrl, getKeywordTabUrl } from '@/lib/mercari'
import MercariProductList from '@/components/MercariProductList.vue'
import type { IMercariProductListItemContent } from '@/components/MercariProductListItem.vue'
import type {
	IMercariProductListItemClickEvent,
	IMercariProductListFetchCompleteEvent,
} from '@/components/MercariProductList.vue'
import {
	fetchMercariCommonInfo,
	fetchInappMercariLandingBanners,
	fetchMercariHotBrands,
	type IMercariHotBrand,
} from '@/assets/ts/apiManagement'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'
import BackButton from '@/components/BackButton.vue'
import {
	formatTrackingKeyword,
	type IMercariLandingBanner,
} from '@/types/Mercari'
import FlowStepsSection from '@/components/ui/FlowStepsSection.vue'
import MercariFaq from '@/pages/shipment-protection-shipping/components/_ShipmentProtectionFAQ.vue'
import type { FAQContent } from '@/types/faq'
import { getSiteBaseUrl } from '@/lib/siteBaseUrl'
import { checkIsSeoEnhancement } from '@/lib/checkService'
import PageBreadCrumb from '@/components/PageBreadCrumb.vue'
import getSeoRedirectLink from '@/lib/getSeoRedirect'
import MercariCircleSlider, {
	type IMercariCircleSliderItem,
} from '@/components/ui/mercari/MercariCircleSlider.vue'

type IMercariCommonInfoCamel = Awaited<
	ReturnType<typeof fetchMercariCommonInfo>
>

const { isSmall, isTabletPortrait, isTabletLandscape } = useBreakpoints()
register()

const sellingPointIcons = ['selling_point1', 'selling_point2', 'selling_point3']

const props = defineProps<{
	pageContent: {
		heroTitle: string
		sellingPointSection: {
			sellingPoint_title: string
			sellingPoint_description: string
		}[]
		trendingKeywordSectionTitle: string
		productItem: IMercariProductListItemContent
		popularCategoriesTitle: string
		hotBrandsTitle: string
		hotBrandsViewAllUrl: string
		hotBrandsViewAllText: string
		productListTitle: string
		productListLoadMoreButtonText: string
		searchPlaceholder: string
		edgeCase: {
			empty: {
				description: string
			}
			serverError: {
				title: string
				description: string
				buttonText: string
			}
		}
		howToBuyContent: {
			sectionTitle: string
			steps: {
				title: string
				description: string
			}[]
		}
		faq?: FAQContent
		about?: {
			title: string
			description: string
		}
		breadcrumbs?: {
			[key: string]: string
		}
	}
}>()

const isSheetOpen = ref(false)
const keywordTabs = ref<{ shopId: string; label: string; value: string }[]>([])
const searchQuery = ref('')
const productListRef = ref<InstanceType<typeof MercariProductList>>()
const commonInfo = ref<IMercariCommonInfoCamel | null>(null)
const popularCategories = ref<IMercariCommonInfoCamel['popularCategories']>([])
const hotBrands = ref<IMercariHotBrand[]>([])
const landingBanners = ref<IMercariLandingBanner['banners']>([])
const isLandingBannersLoaded = ref(false)
const bannerSwiperEl = ref<SwiperContainer | null>(null)
const howToBuySteps = computed(() =>
	props.pageContent.howToBuyContent.steps.map((step, index) => ({
		...step,
		icon: howToBuySectionIcons[index],
	})),
)
const howToBuySectionIcons = [
	'flow_place_order',
	'flow_payment',
	'flow_consolidate',
	'flow_delivery',
]
const isSeoEnhancement = checkIsSeoEnhancement()

const isAPIError = computed(() => {
	return productListRef.value?.state.status === 'error'
})

const popularCategoriesItems = computed(() => {
	return popularCategories.value.map(category => ({
		id: category.category_id,
		image_url: category.image_url,
		display_name: category.display_name,
		url: getMercariUrl('search', {
			category_id: String(category.category_id),
		}),
	}))
})

const hotBrandsItems = computed(() => {
	return hotBrands.value.map(brand => ({
		id: brand.brand_id,
		image_url:
			brand.image || getVueAssetImagePath('mercari-brand-default-bg.png'),
		display_name: brand.name,
		url: getMercariUrl('brand-listing', {
			brand_slug: brand.slug,
		}),
	}))
})

const breadcrumbs = computed(() => {
	return [
		{
			displayName: props.pageContent.breadcrumbs?.index ?? '',
			categoryId: 'home',
			link: import.meta.env.DEV ? '/' : getSiteBaseUrl(),
		},
		{
			displayName: props.pageContent.breadcrumbs?.shopping ?? '',
			categoryId: 'shopping',
			link: import.meta.env.DEV
				? '/proxy'
				: `${getSiteBaseUrl()}/${getSeoRedirectLink('proxyShopping')}/`,
		},
		{
			displayName: props.pageContent.breadcrumbs?.mercariIndex ?? '',
			categoryId: 'mercari-index',
			link: getMercariUrl('index'),
		},
	]
})

const handleItemClick = (data: IMercariProductListItemClickEvent) => {
	const formattedKeyword = formatTrackingKeyword(false, '', searchQuery.value)
	createGAEvent('select_item', {
		page_name: GaGeneralPageName.MercariIndex,
		items: [
			{
				location_id: GaGeneralPageName.MercariIndex,
				item_list_name: 'Product Listing-Recommendation',
				item_id: `product_${data.item.id}`,
				item_name: data.item.name,
				item_category: 'Mercari',
				index: data.index + 1,
				keyword: formattedKeyword,
			},
		],
	})

	window.open(data.targetUrl, '_blank')
}

const handleFetchComplete = (data: IMercariProductListFetchCompleteEvent) => {
	if (!data.success) {
		createGAEvent(GaGeneralEvent.ButtonClick, {
			page_name: GaGeneralPageName.MercariIndex,
			button_name: 'Try Again',
		})
	}
}

const handleKeywordTabClick = (value: string) => {
	const selectedTab = keywordTabs.value.find(tab => tab.value === value)

	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariIndex,
		button_name: 'Search Submitted',
		keyword: value,
		event_extra: selectedTab?.shopId ? 'Shop' : 'Trending',
	})

	window.location.href = getKeywordTabUrl(value, keywordTabs.value)
}

const handleSearch = (value: string) => {
	if (value.trim()) {
		handleSearchSubmittedGAevent(value)

		const searchUrl = getMercariUrl('search', { searchQuery: value })
		window.location.href = searchUrl
	}
	isSheetOpen.value = false
}

const handleSearchInputClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariIndex,
		button_name: 'Search',
	})
}

// Use scroll position
const { y: scrollY } = useWindowScroll()

// Show back button when scrolled past product list
const isShowBackToTopButton = computed(() => {
	return scrollY.value > window.innerHeight * 0.33
})

const scrollToTop = () => {
	window.scrollTo({
		top: 0,
		behavior: 'smooth',
	})
}

const handleSearchSubmittedGAevent = (value: string) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariIndex,
		button_name: 'Search Submitted',
		keyword: value,
		event_extra: isSheetOpen.value ? 'Recommendation' : 'Keyword',
	})
}

const closeSheet = () => {
	isSheetOpen.value = false
}

const fetchCommonInfo = async () => {
	try {
		commonInfo.value = await fetchMercariCommonInfo()

		setKeywordTabs()
		setPopularCategories()
	} catch (error) {
		console.error('Failed to fetch mercari common info:', error)
	}
}

const setKeywordTabs = () => {
	const MAX_KEYWORD_TABS = 6

	if (!commonInfo.value) return

	const promotionShops = commonInfo.value.promotionShops.map(shop => ({
		shopId: shop.shop_id,
		label: shop.display_name,
		value: shop.display_name,
	}))
	const keywords = commonInfo.value.keywords.map(keyword => ({
		shopId: '',
		label: keyword,
		value: keyword,
	}))

	keywordTabs.value = [...promotionShops, ...keywords].slice(
		0,
		MAX_KEYWORD_TABS,
	)
}

const setPopularCategories = () => {
	if (!commonInfo.value) return
	popularCategories.value = commonInfo.value.popularCategories
}

const setLandingBanners = async () => {
	try {
		const { banners } = await fetchInappMercariLandingBanners()
		landingBanners.value = banners
	} catch (error) {
		console.error('Failed to set Mercari landing banners:', error)
	} finally {
		isLandingBannersLoaded.value = true
	}
}

const setHotBrands = async () => {
	try {
		hotBrands.value = await fetchMercariHotBrands()
	} catch (error) {
		console.error('Failed to fetch Mercari hot brands:', error)
	}
}

const prevSlide = () => {
	if (bannerSwiperEl.value) {
		bannerSwiperEl.value.swiper.slidePrev()
	}
}

const nextSlide = () => {
	if (bannerSwiperEl.value) {
		bannerSwiperEl.value.swiper.slideNext()
	}
}

const handleMercariLogoClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariIndex,
		button_name: 'Merchant Logo',
	})
}

const handleBannerClick = (bannerTitle: string) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariIndex,
		button_name: 'Campaign Banner',
		campaign_name: bannerTitle,
	})
}

const handleCategoryClick = (item: IMercariCircleSliderItem) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariIndex,
		button_name: 'Filtering',
		event_extra: 'Popular',
		item_category: String(item.id),
	})
}

const handleHotBrandClick = (item: IMercariCircleSliderItem) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariIndex,
		button_name: 'Hot Brands',
		event_extra: item.id,
	})
}

const handleHotBrandsViewAllClick = () => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.MercariIndex,
		button_name: 'See More Brands',
	})
}

onMounted(() => {
	fetchCommonInfo()
	setLandingBanners()
	setHotBrands()

	createGAEvent(GaGeneralEvent.PageLoad, {
		page_name: GaGeneralPageName.MercariIndex,
	})
})

// Close sheet when tablet/desktop
watch(isSmall, newValue => {
	if (!newValue) {
		isSheetOpen.value = false
	}
})
</script>

<template>
	<div>
		<template v-if="!isAPIError">
			<PageBreadCrumb
				:breadcrumbs="breadcrumbs"
				:is-override-seo-check="true"
				class="py-16px lg:pt-32px"
			/>
			<MercariSearchBar
				v-model="searchQuery"
				v-model:isSheetOpen="isSheetOpen"
				:placeholder="props.pageContent.searchPlaceholder"
				:keyword-tabs="keywordTabs"
				:trending-keyword-section-title="
					props.pageContent.trendingKeywordSectionTitle
				"
				:is-small="isSmall"
				:is-tablet-portrait="isTabletPortrait"
				@search="handleSearch"
				@search-click="handleSearchInputClick"
				@close-sheet="closeSheet"
				@keyword-tab-click="handleKeywordTabClick"
				@mercari-logo-click="handleMercariLogoClick"
				class="lg:mb-32px"
				:class="isSeoEnhancement ? undefined : 'lg:mt-32px'"
			/>
		</template>

		<section
			v-if="
				!isAPIError && (!isLandingBannersLoaded || landingBanners.length > 0)
			"
		>
			<div
				class="relative aspect-[4/3] bg-n-75 mdp:aspect-[3/1] lg:mx-auto lg:w-[1204px] lg:rounded-t-[8px]"
			>
				<swiper-container
					v-if="landingBanners.length > 0"
					ref="bannerSwiperEl"
					:loop="landingBanners.length > 1"
					slides-per-view="1"
					autoplay-delay="3000"
					class="h-full w-full lg:overflow-hidden lg:rounded-t-[8px]"
				>
					<swiper-slide v-for="(banner, index) in landingBanners" :key="index">
						<a :href="banner.link" @click="handleBannerClick(banner.title)">
							<img
								:src="
									!isTabletLandscape
										? banner.image.mobile
										: banner.image.desktop
								"
								alt="Marketing Campaign"
								class="h-full w-full select-none object-cover"
								:loading="index ? 'lazy' : 'eager'"
							/>
						</a>
					</swiper-slide>
				</swiper-container>

				<BaseIcon
					v-if="landingBanners.length > 1"
					name="chevron_round"
					class="absolute left-[10px] top-1/2 z-[1] h-[40px] w-[40px] -translate-y-1/2 rotate-180 cursor-pointer mdp:left-[20px] lg:left-[-20px]"
					@click="prevSlide"
				/>

				<BaseIcon
					v-if="landingBanners.length > 1"
					name="chevron_round"
					class="absolute right-[10px] top-1/2 z-[1] h-[40px] w-[40px] -translate-y-1/2 cursor-pointer mdp:right-[20px] lg:right-[-20px]"
					@click="nextSlide"
				/>
			</div>
		</section>

		<template v-if="!isAPIError">
			<section class="text-n-950">
				<div class="bs-container bg-t-75 lg:rounded-b-[8px]">
					<div
						class="col-span-12 py-[16px] mdp:py-[20px] mdl:px-[10.5px] lg:px-[88.5px]"
					>
						<div
							class="flex flex-wrap gap-close-friends mdp:justify-center mdp:gap-casual-friends mdl:justify-start mdl:gap-20px lg:gap-32px"
						>
							<template
								v-for="(item, index) in props.pageContent.sellingPointSection"
								:key="index"
							>
								<div
									v-if="!isTabletLandscape"
									class="flex items-center gap-bff mdp:gap-best-friends"
								>
									<BaseIcon
										name="success_circle3"
										class="h-20px w-20px flex-shrink-0"
									/>
									<div
										:class="isSmall ? 'typography-s2-str' : 'typography-s1-str'"
									>
										{{ item.sellingPoint_title }}
									</div>
								</div>
								<div
									v-else
									class="flex flex-1 items-center gap-16px lg:gap-20px"
								>
									<BaseIcon
										:name="sellingPointIcons[index]"
										class="h-48px w-48px flex-shrink-0 mdl:h-[64px] mdl:w-[64px]"
									/>
									<div class="flex flex-col gap-4px">
										<div class="typography-s1-str">
											{{ item.sellingPoint_title }}
										</div>
										<div class="typography-s2-reg text-n-400">
											{{ item.sellingPoint_description }}
										</div>
									</div>
								</div>
							</template>
						</div>
					</div>
				</div>
			</section>
		</template>

		<MercariCircleSlider
			v-if="!isAPIError && popularCategories?.length > 0"
			:title="props.pageContent.popularCategoriesTitle"
			:items="popularCategoriesItems"
			@item-click="handleCategoryClick"
		/>

		<MercariCircleSlider
			v-if="!isAPIError && hotBrands?.length > 0"
			:title="props.pageContent.hotBrandsTitle"
			:items="hotBrandsItems"
			:view-all-url="props.pageContent.hotBrandsViewAllUrl"
			:view-all-text="props.pageContent.hotBrandsViewAllText"
			@item-click="handleHotBrandClick"
			@view-all-click="handleHotBrandsViewAllClick"
		/>

		<section class="bs-container">
			<div class="col-span-12 pb-[37px] pt-20px mdp:py-[60px]">
				<template v-if="!isAPIError">
					<div
						class="mb-16px text-n-950 mdp:mb-casual-friends"
						:class="{
							'typography-s1-str': isSmall,
							'typography-h4-str': isTabletPortrait,
						}"
					>
						{{ props.pageContent.productListTitle }}
					</div>
				</template>

				<MercariProductList
					ref="productListRef"
					:item-content="props.pageContent.productItem"
					:edge-case="props.pageContent.edgeCase"
					:load-more-button-text="
						props.pageContent.productListLoadMoreButtonText
					"
					@item-click="handleItemClick"
					@fetch-complete="handleFetchComplete"
				/>

				<div
					class="fixed bottom-[88px] right-20px"
					v-show="isShowBackToTopButton"
				>
					<BackButton @click="scrollToTop" />
				</div>
			</div>
		</section>

		<section v-if="!isAPIError" class="bs-container gap-y-0 pb-[60px]">
			<div class="col-span-12 mb-16px mdp:mb-32px">
				<div class="typography-h4-str">
					{{ props.pageContent.howToBuyContent.sectionTitle }}
				</div>
			</div>

			<div
				class="col-span-12 rounded-[8px] bg-o-50 px-16px py-32px mdp:px-0 mdp:py-48px"
			>
				<FlowStepsSection :steps="howToBuySteps" />
			</div>
		</section>

		<template v-if="!isAPIError && isSeoEnhancement">
			<section class="bg-o-75 py-distant-acquaintances">
				<div class="bs-container">
					<div class="col-span-12">
						<div class="flex flex-wrap gap-20px">
							<h2
								class="typography-h2-str basis-full text-center text-n-950 mdp:mr-24px mdp:basis-[282px] mdp:text-left lg:basis-[384px]"
							>
								{{ props.pageContent.about?.title ?? '' }}
							</h2>
							<p class="typography-b2-reg flex-1 text-n-800">
								{{ props.pageContent.about?.description ?? '' }}
							</p>
						</div>
					</div>
				</div>
			</section>

			<section class="bg-o-50 py-distant-acquaintances">
				<div class="bs-container">
					<div class="col-span-12">
						<MercariFaq
							theme="mercari-index"
							:content="props.pageContent.faq"
						/>
					</div>
				</div>
			</section>
		</template>
	</div>
</template>
